import React, { useRef, useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import axios, { AxiosRequestConfig } from "axios";
import { Button } from "primereact/button";
import { FileUploadOptions, FileUpload } from "primereact/fileupload";
import { Message } from "primereact/message";
import { Panel } from "primereact/panel";
import { ProgressSpinner } from "primereact/progressspinner";
import { validateGTIN } from "../../utils/SyndigoUtils.js";
import "./SyndigoUpload.css";

const { REACT_APP_THINKHAUS_URL
} = process.env

type SyndigoUploadProps = {
    uploadURL: string,
};

type FilenameMatch = {
    gtin: string,
    carouselNumber: number,
    position: string,
};

type UploadFile = {
    fileData: File,
    filenameMatch: FilenameMatch | null,
    uploading: boolean,
    success: boolean | null,
    error: string | null,
};

const carouselPositions: Record<number, string> = {
    1: "Main Product Image",
    2: "Detailed Product View 1",
    3: "Detailed Product View 2",
    4: "Detailed Product View 3",
    5: "Detailed Product View 4",
    6: "Detailed Product View 5",
};

const getFilenameMatch = (fileName: string): FilenameMatch | null => {
    const match = /^RHP_(\d{13,14})(?:_.*)?_Carousel([1-6])\.(jpg|png)$/i.exec(fileName);
    if (!match) {
        return null;
    }

    const carouselNumber = Number(match[2]);
    return {
        gtin: validateGTIN(match[1]),
        carouselNumber,
        position: carouselPositions[carouselNumber],
    };
};

const sortFilesForDisplay = <T extends File,>(files: T[]): T[] => {
    return [...files].sort((firstFile, secondFile) =>
        Number(getFilenameMatch(firstFile.name) !== null) - Number(getFilenameMatch(secondFile.name) !== null)
    );
};

export const SyndigoUpload = ({ uploadURL }: SyndigoUploadProps) => {
    const { session } = useAuth();

    const fileUploadRef = useRef<FileUpload | null>(null);
    const [totalFileSize, setTotalFileSize] = useState(0);
    const [files, setFiles] = useState<UploadFile[]>([]);
    const [errors, setErrors] = useState({ files: [], position: false, totalFileSize: false, isGlobalGTIN: false });

    const maxSizeInMB = 100 //MB
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024; //100 MB of all files

    const onBeforeSelect = (e) => {
        let event = e.originalEvent;
        let newSize = totalFileSize;

        let files = [];
        if (event.type === 'drop') {
            event = event as DragEvent;
            files = Array.from(event.dataTransfer?.files!);
        } else {
            event = event as React.ChangeEvent<HTMLInputElement>;
            files = Array.from(event.target.files!);
        }

        files.forEach(file => { // Compute sum of file sizes to add to state variable
            newSize += file.size;
        });

        if (newSize > maxSizeInBytes) {
            setErrors(prev => ({ ...prev, totalFileSize: true }));
            return false;
        } else {
            setTotalFileSize(newSize)
            setErrors({ files: [], position: false, totalFileSize: false, isGlobalGTIN: false }); // Clear any previous errors
        }
    };

    const onSelect = (e) => {
        fileUploadRef.current?.setFiles(sortFilesForDisplay(e.files));

        const uploadedFiles = (Array.from(e.files) as File[]).map(file => ({
            fileData: file,
            filenameMatch: getFilenameMatch(file.name),
            uploading: false,
            success: null,
            error: null
        }));

        setFiles(prevFiles => {
            const newFiles = uploadedFiles.filter(uploadedFile =>
                !prevFiles.some(existingFile => existingFile.fileData.name === uploadedFile.fileData.name));
            return [...prevFiles, ...newFiles];
        });
    };

    const onUpload = async (e) => {

        const uploadPromises = files.map(async (file) => {

            const formData = new FormData();
            formData.append('image', file.fileData);
            if (file.filenameMatch) {
                formData.append('gtin', file.filenameMatch.gtin);
                formData.append('position', file.filenameMatch.position);
            }

            setFiles(prevFiles => prevFiles.map(f =>
                f.fileData.name === file.fileData.name ? { ...f, uploading: true } : f
            ));

            try {
                const config: AxiosRequestConfig = {
                    headers: {
                        "Authorization": `Bearer ${session.access_token}`,
                        "Content-Type": "multipart/form-data"
                    }
                }

                const destination = file.filenameMatch ? 'api/syndigo-image-upload' : uploadURL;
                await axios.post(REACT_APP_THINKHAUS_URL + destination, formData, config);

                setFiles(prevFiles => prevFiles.map(f =>
                    f.fileData.name === file.fileData.name ? { ...f, uploading: false, success: true } : f
                ));
            } catch (error) {
                let errorText = 'There was an error uploading your image.'

                if (error?.response?.status === 429) {
                    errorText = error.response.data.message;
                } else if (error?.response?.data?.errors) {
                    const responseErrors = error.response.data.errors;
                    errorText = Array.isArray(responseErrors) ? responseErrors.join(', ') : responseErrors;
                } else if (error?.response?.data?.message) {
                    errorText = error.response.data.message;
                }

                setFiles(prevFiles => prevFiles.map(f =>
                    f.fileData.name === file.fileData.name ? { ...f, uploading: false, success: false, error: errorText } : f
                ));
            }
        });

        await Promise.all(uploadPromises);
    };

    const onRemove = (file) => {
        setFiles((prevFiles) => prevFiles.filter(f => f.fileData.name !== file.file.name));
    };

    const onClear = () => {
        setFiles([]);
        setTotalFileSize(0)
        setErrors({ files: [], position: false, totalFileSize: false, isGlobalGTIN: false })
    };

    const cancelOptions: FileUploadOptions = {
        className: 'syndigo-upload-clear',
        icon: 'pi pi-times'
    }

    const uploadOptions: FileUploadOptions = {
        className: 'syndigo-upload-submit'
    }

    const headerTemplate = (options: any) => {
        return (
            <div>
                <div className="syndigo-upload-toolbar flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="syndigo-upload-actions flex flex-wrap align-items-center gap-2">
                        <div data-testid="Choose-Button">{options.chooseButton}</div>
                        <div data-testid="Cancel-Button">{options.cancelButton}</div>
                    </div>
                    <div className="syndigo-upload-primary-action" data-testid="Upload-Button">{options.uploadButton}</div>
                </div>
                {errors.totalFileSize && <Message className="syndigo-upload-limit-error" severity="error" text={`Total file size exceeds the ${maxSizeInMB} MB limit`} />}
            </div>
        );
    };

    const itemTemplate = (file, options) => {

        const fileInState = files.find(f => f.fileData.name === file.name);
        const filenameMatch = fileInState?.filenameMatch;
        const hasUploadError = fileInState?.success === false;

        const renderUploadStatus = () => {
            if (fileInState?.uploading) {
                return (
                    <div className="syndigo-file-uploading flex align-items-center gap-2" aria-live="polite">
                        <ProgressSpinner aria-label="Uploading image" style={{ width: '20px', height: '20px' }} strokeWidth="8" />
                        <span>Uploading</span>
                    </div>
                );
            }
            if (fileInState && fileInState.success !== null) {
                if (fileInState.success) {
                    return <Message data-testid="upload-success-message" className="syndigo-upload-result" severity="success" text={filenameMatch ? `Assigned to ${filenameMatch.position}` : "Saved to assets only · Not assigned to a UPC"} />;
                } else {
                    return (
                        <div className="syndigo-file-error">
                            <Panel className="syndigo-file-error-panel custom-error-panel" header="Error uploading file" collapsed={true} toggleable>
                                <p className="m-0 text-left" data-testid="fileInState-error-text">
                                    {fileInState.error}
                                </p>
                            </Panel>
                        </div>
                    )

                }
            }
            return null;
        };

        return (
            <div key={file.name} className={`syndigo-upload-row ${hasUploadError ? 'syndigo-upload-row-error' : ''}`}>
                <div className="syndigo-file-main flex align-items-center gap-3">
                    <div className="syndigo-file-preview flex align-items-center justify-content-center">
                        <img alt="" aria-hidden="true" src={file.objectURL} />
                    </div>
                    <div className="syndigo-file-details flex flex-column gap-1">
                        <div className="syndigo-file-heading flex flex-wrap align-items-baseline gap-2">
                            <span className="syndigo-file-name">{file.name}</span>
                            <span className="syndigo-file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                        {filenameMatch
                            ? <div className="syndigo-filename-status" data-testid={`filename-status-${file.name}`}><i className="pi pi-check-circle" aria-hidden="true" /><span>Filename matches pattern · Carousel {filenameMatch.carouselNumber} → {filenameMatch.position}</span></div>
                            : <div className="syndigo-filename-status syndigo-filename-warning" data-testid={`filename-status-${file.name}`}><i className="pi pi-info-circle" aria-hidden="true" /><span>Filename does not match pattern · This image will be saved to assets only and will not be assigned to a UPC.</span></div>}
                    </div>
                </div>
                <div className="syndigo-file-actions flex align-items-center gap-3">
                    {!hasUploadError && <div className="syndigo-file-status">{renderUploadStatus()}</div>}
                    <Button
                        type="button"
                        icon="pi pi-trash"
                        className="syndigo-remove-button p-button-rounded p-button-text p-button-secondary"
                        onClick={() => options.onRemove(fileInState)}
                        aria-label={`Remove ${file.name}`}
                        title="Remove file"
                        data-testid={`Trash-${file.name}`}
                    />
                </div>
                {hasUploadError && <div className="syndigo-file-error-row">{renderUploadStatus()}</div>}
            </div>
        );
    };

    return (
        <main className="syndigo-upload">
            <div className="syndigo-upload-shell flex flex-column w-full mx-auto gap-4">
                <header className="syndigo-upload-intro flex flex-column md:flex-row align-items-start md:align-items-end justify-content-between gap-4">
                    <div className="syndigo-upload-intro-copy flex flex-column gap-2">
                        <div className="syndigo-upload-eyebrow flex align-items-center gap-2">
                            <i className="pi pi-images" aria-hidden="true" />
                            <span>Syndigo</span>
                        </div>
                        <h1 className="syndigo-upload-title m-0">Image Upload</h1>
                        <p className="syndigo-upload-description m-0">Add product images and let filename matching handle position assignment.</p>
                    </div>
                </header>
                <section className="syndigo-upload-guidance flex align-items-start gap-3" role="note" aria-labelledby="syndigo-filename-guidance">
                    <div className="syndigo-guidance-icon flex align-items-center justify-content-center">
                        <i className="pi pi-info-circle" aria-hidden="true" />
                    </div>
                    <div className="syndigo-guidance-content flex flex-column gap-2">
                        <div className="syndigo-guidance-heading flex flex-wrap align-items-center gap-2">
                            <h2 id="syndigo-filename-guidance" className="m-0">Filename guidance</h2>
                            <span className="syndigo-guidance-badge">Automatic UPC matching</span>
                        </div>
                        <p className="syndigo-guidance-copy m-0">Use <code>RHP_[UPC]_Carousel{`{number}`}.jpg</code> or <code>.png</code>; the UPC may be 13 or 14 digits. Additional underscore-separated parts, including periods, are allowed before the final <code>_Carousel{`{number}`}</code> (for example, <code>RHP_00001111067808_Chicken_Salad_Snack_Tray_5.27oz_Carousel1.jpg</code>).</p>
                        <p className="syndigo-guidance-copy syndigo-guidance-secondary m-0">Files that do not match the pattern will be uploaded to the generic assets folder and will not be assigned to a UPC.</p>
                    </div>
                </section>
                <section className="syndigo-upload-card" aria-labelledby="syndigo-upload-files-heading">
                    <div className="syndigo-upload-card-heading flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
                        <div className="flex flex-column gap-1">
                            <h2 id="syndigo-upload-files-heading" className="m-0">Choose images</h2>
                            <p className="m-0">Review filename matching before sending your files to Syndigo.</p>
                        </div>
                        <div className="syndigo-upload-limits flex flex-wrap gap-2">
                            <span className="syndigo-upload-limit"><i className="pi pi-file" aria-hidden="true" />10 MB per file</span>
                            <span className="syndigo-upload-limit"><i className="pi pi-database" aria-hidden="true" />100 MB total</span>
                        </div>
                    </div>
                    <FileUpload
                        ref={fileUploadRef}
                        className="syndigo-file-upload"
                        name="demo[]"
                        url="./upload"
                        multiple
                        accept="image/*"
                        customUpload
                        maxFileSize={10000000} // 10MB limit per file
                        chooseLabel="Select images"
                        cancelLabel="Clear all"
                        uploadLabel="Upload to Syndigo"
                        uploadHandler={onUpload}
                        onBeforeSelect={onBeforeSelect}
                        onSelect={onSelect}
                        onRemove={onRemove}
                        onClear={onClear}
                        cancelOptions={cancelOptions}
                        uploadOptions={uploadOptions}
                        emptyTemplate={
                            <div className="syndigo-upload-empty flex flex-column align-items-center text-center gap-2">
                                <div className="syndigo-upload-empty-icon flex align-items-center justify-content-center">
                                    <i className="pi pi-cloud-upload" aria-hidden="true" />
                                </div>
                                <strong>Drop your images here</strong>
                                <span>Drag and drop files or select them from your device.</span>
                                <small>Matching JPG and PNG filenames are assigned to a UPC automatically.</small>
                            </div>
                        }
                        itemTemplate={itemTemplate}
                        headerTemplate={headerTemplate}
                        data-testid="PR-File-Upload"
                    />
                </section>
            </div>
        </main>
    );
}